"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface Item {
  id: number;
  projectId: number;
  projectName: string | null;
  actorUserId: number;
  actorName: string | null;
  kind: "daily_update" | "todo_added" | "sub_added" | "sub_closed" | "master_closed" | string;
  title: string;
  detail: string | null;
  readAt: string | null;
  createdAt: string;
}

const KIND_META: Record<string, { bg: string; border: string; emoji: string; label: string }> = {
  daily_update:          { bg: "#EFF6FF", border: "#378ADD", emoji: "📝", label: "打進度" },
  todo_added:            { bg: "#F0FDF4", border: "#16A34A", emoji: "➕", label: "新增待辦" },
  sub_added:             { bg: "#EEEDFE", border: "#7C3AED", emoji: "🌲", label: "新增子案" },
  sub_closed:            { bg: "#F1F5F9", border: "#64748B", emoji: "✓",  label: "子案結案" },
  master_closed:         { bg: "#FFF7ED", border: "#EA580C", emoji: "🎯", label: "母結案" },
  sub_assigned_helper:   { bg: "#FEF3C7", border: "#D97706", emoji: "🤝", label: "指派你為協作人" },
  sub_assigned_assignee: { bg: "#FEE2E2", border: "#DC2626", emoji: "📌", label: "指派你為指派人" },
  // 4 種新 kind（v2）
  alert_stale:           { bg: "#FEE2E2", border: "#DC2626", emoji: "⚠",  label: "未更新警示" },
  flagged:               { bg: "#FFF7ED", border: "#EA580C", emoji: "🚩", label: "被標記" },
  deadline_soon:         { bg: "#EEEDFE", border: "#7C3AED", emoji: "📅", label: "截止日將至" },
  daily_nudge:           { bg: "#F0FDF4", border: "#16A34A", emoji: "📮", label: "當日未更新" },
  // 🆕 Phase 2 2026-10-14:單部門被 PM 退回
  dept_rejected:         { bg: "#FEE2E2", border: "#DC2626", emoji: "↩", label: "部門退回待補" },
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return "剛剛";
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m} 分鐘前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小時前`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} 天前`;
  return iso.slice(0, 10);
}

// 🆕 2026-10-27 A3 · 分類 tab:actionable(等我處理) / rejected(已退回) / other
type FilterTab = "all" | "action" | "rejected" | "other";
const ACTIONABLE_KINDS = new Set([
  "leader_setup_needed",
  "leader_fill_waiting",
  "submitted_for_review",
  "pm_reviewed",
  "director_closed",
  "sub_assigned_helper",
  "sub_assigned_assignee",
]);
const REJECTED_KINDS = new Set([
  "rejected",
  "dept_rejected",
  "task_rejected",
]);

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<FilterTab>("all");
  const [items, setItems] = useState<Item[]>([]);
  const [unread, setUnread] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);

  // 響應式：< 640px 走 mobile sheet
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const upd = () => setIsMobile(mq.matches);
    upd();
    mq.addEventListener("change", upd);
    return () => mq.removeEventListener("change", upd);
  }, []);

  // 🔒 Bug fix AN5：AbortController 防慢速網路時舊 fetch 覆蓋新資料 + unmount leak
  const abortRef = useRef<AbortController | null>(null);
  async function load() {
    try {
      // 中止前一個 in-flight 請求
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      // 🆕 2026-10-14:只拉未讀 · 已讀的「全部標為已讀」或單筆已讀後直接從列表消失 · 不再變灰堆積
      const res = await fetch("/api/notifications?unread=1", {
        credentials: "include",
        cache: "no-store",
        signal: ac.signal,
      });
      if (!res.ok) return;
      const j = await res.json();
      // 若已被中止 · 丟掉這次結果（避免 setState on unmounted）
      if (ac.signal.aborted) return;
      setItems(j.items ?? []);
      setUnread(j.unreadCount ?? 0);
    } catch (e) {
      // AbortError 是正常的 · 忽略
      if (e instanceof DOMException && e.name === "AbortError") return;
    }
  }

  // 30 秒 polling
  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => {
      clearInterval(t);
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 點外面關閉
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // desktop：打開時計算面板位置 → position:fixed 錨在鈴鐺右邊，避免被 sidebar overflow 裁切
  useEffect(() => {
    if (!open || isMobile) {
      setPanelPos(null);
      return;
    }
    const compute = () => {
      const btn = btnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const panelW = 340;
      const margin = 8;
      // 預設：鈴鐺右邊 + 8px
      let left = r.right + margin;
      // 若溢出右邊，改對齊到鈴鐺下方靠左
      if (left + panelW + 12 > window.innerWidth) {
        left = Math.max(12, window.innerWidth - panelW - 12);
      }
      setPanelPos({ top: r.top, left });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, isMobile]);

  async function markOne(id: number) {
    try {
      const r = await fetch("/api/notifications/mark-read", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      if (!r.ok) return;
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, readAt: new Date().toISOString() } : x)));
      setUnread((u) => Math.max(0, u - 1));
      // 🆕 2026-10-12:重讀 server 權威值 · 避免 polling 衝突
      void load();
    } catch { /* noop */ }
  }
  async function markAll() {
    try {
      const r = await fetch("/api/notifications/mark-read", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ all: true }),
      });
      if (!r.ok) return;
      // 🆕 2026-10-14:全部標為已讀 = 從列表清除(不是變灰);仍會從 server 重讀(load) · 若有新未讀通知會補回
      setItems([]);
      setUnread(0);
      void load();
    } catch { /* noop */ }
  }

  return (
    <div ref={panelRef} style={{ position: "relative" }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          position: "relative",
          background: "transparent",
          border: "0.5px solid var(--border-strong)",
          borderRadius: 8,
          padding: "6px 10px",
          cursor: "pointer",
          fontSize: 18,
          lineHeight: 1,
        }}
        aria-label={`通知 (${unread})`}
      >
        🔔
        {unread > 0 && (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              background: "#DC2626",
              color: "#fff",
              fontSize: 9,
              padding: "1px 5px",
              borderRadius: 8,
              fontWeight: 600,
              minWidth: 16,
              textAlign: "center",
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <>
          {/* 桌機 / 手機都加 backdrop（點外面關閉 + 遮罩讓 gantt 內容看不出來）*/}
          <div
            onClick={() => setOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: isMobile ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.15)",
              zIndex: 9998,
            }}
          />
          <div
            style={
              isMobile
                ? {
                    position: "fixed",
                    top: 12,
                    left: 12,
                    right: 12,
                    maxHeight: "calc(100vh - 24px)",
                    overflowY: "auto",
                    background: "#FFFFFF",
                    border: "0.5px solid var(--border)",
                    borderRadius: 12,
                    boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
                    zIndex: 9999,
                    isolation: "isolate",
                  }
                : {
                    position: "fixed",
                    top: panelPos?.top ?? 60,
                    left: panelPos?.left ?? 220,
                    width: 340,
                    maxHeight: "calc(100vh - 24px)",
                    overflowY: "auto",
                    background: "#FFFFFF",
                    border: "1px solid #CBD5E1",
                    borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                    zIndex: 9999,
                    isolation: "isolate",
                  }
            }
          >
          <div
            style={{
              padding: "10px 14px",
              background: "#F1F5F9",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: 12,
              borderBottom: "0.5px solid var(--border)",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <span style={{ fontWeight: 600 }}>
              通知（{unread} 未讀）
            </span>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {unread > 0 && (
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={markAll}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#378ADD",
                    fontSize: 11,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  全部標為已讀
                </button>
              )}
              {isMobile && (
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  style={{
                    background: "transparent",
                    border: "0.5px solid var(--border-strong)",
                    color: "var(--text-primary)",
                    fontSize: 14,
                    cursor: "pointer",
                    borderRadius: 6,
                    padding: "2px 8px",
                    lineHeight: 1,
                  }}
                  aria-label="關閉"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* 🆕 2026-10-27 A3 · 分類 tab */}
          <div style={{ display: "flex", gap: 4, padding: "6px 10px", background: "#F8FAFC", borderBottom: "0.5px solid var(--border)", fontSize: 11 }}>
            {([
              { k: "all", label: "全部", count: items.length },
              { k: "action", label: "待我處理", count: items.filter((n) => ACTIONABLE_KINDS.has(n.kind)).length },
              { k: "rejected", label: "已退回", count: items.filter((n) => REJECTED_KINDS.has(n.kind)).length },
              { k: "other", label: "其他", count: items.filter((n) => !ACTIONABLE_KINDS.has(n.kind) && !REJECTED_KINDS.has(n.kind)).length },
            ] as const).map((t) => {
              const active = tab === t.k;
              return (
                <button key={t.k} type="button" onClick={() => setTab(t.k)}
                  style={{
                    padding: "3px 8px", borderRadius: 10, fontSize: 10, fontWeight: 600,
                    background: active ? "#378ADD" : "transparent",
                    color: active ? "#fff" : "#64748B",
                    border: active ? "none" : "0.5px solid #CBD5E1",
                    cursor: "pointer",
                  }}>
                  {t.label} ({t.count})
                </button>
              );
            })}
          </div>

          {items.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--text-muted)", fontSize: 12 }}>
              🎉 沒有通知
            </div>
          ) : (
            items.filter((n) => {
              if (tab === "all") return true;
              if (tab === "action") return ACTIONABLE_KINDS.has(n.kind);
              if (tab === "rejected") return REJECTED_KINDS.has(n.kind);
              return !ACTIONABLE_KINDS.has(n.kind) && !REJECTED_KINDS.has(n.kind);
            }).map((n) => {
              const meta = KIND_META[n.kind] ?? { bg: "#F8F9FB", border: "#94A3B8", emoji: "🔔", label: "" };
              const isRead = !!n.readAt;
              return (
                <Link
                  key={n.id}
                  href={`/projects/${n.projectId}`}
                  onClick={() => !isRead && markOne(n.id)}
                  style={{
                    display: "block",
                    padding: "10px 14px",
                    background: isRead ? "#F8FAFC" : meta.bg,
                    borderLeft: `3px solid ${meta.border}`,
                    borderTop: "0.5px solid var(--border)",
                    textDecoration: "none",
                    color: isRead ? "#94A3B8" : "#111827", // 已讀改灰字，不用 opacity
                    transition: "background 0.15s",
                  }}
                >
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        background: isRead ? "#CBD5E1" : meta.border,
                        color: "#fff",
                        borderRadius: 50,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        flexShrink: 0,
                      }}
                    >
                      {n.actorName?.slice(0, 1) ?? meta.emoji}
                    </div>
                    <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5, minWidth: 0 }}>
                      <div style={{ wordBreak: "break-word" }}>
                        {n.actorName && <b>{n.actorName}</b>}
                        {n.actorName && " · "}
                        {n.projectName && <b>{n.projectName}</b>}
                        {n.projectName && " · "}
                        {n.title}
                      </div>
                      {n.detail && (
                        <div
                          style={{
                            color: isRead ? "#94A3B8" : "#374151",
                            fontSize: 11,
                            marginTop: 3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {n.detail}
                        </div>
                      )}
                      <div style={{ color: isRead ? "#94A3B8" : "var(--text-muted)", fontSize: 10, marginTop: 3 }}>
                        {meta.emoji} {meta.label} · {relativeTime(n.createdAt)}
                        {isRead && " · 已讀"}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })
          )}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
